// ManagerReview - Manager dashboard to review and manage team expense submissions
// Features: Search by employee name, filter by status, view all or pending only

import { getStatusColor, getStatusLabel } from '../../utils/statusHelpers';
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { formatSubmissionPeriod } from '../../utils/dateHelpers';
import ViewReceiptDialog from '../receipts/ViewReceiptDialog';
import { useNavigate } from 'react-router-dom';
import { submissionsAPI, receiptsAPI } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';
import { useUser } from '../../contexts/UserContext';
import type { Submission, Receipt } from '../../types';
import dayjs from 'dayjs';

const ManagerReview: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { isManager } = useUser();

  // State
  const [allSubmissions, setAllSubmissions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewReceiptDialogOpen, setViewReceiptDialogOpen] = useState(false);
  const [selectedReceipt, setSelectedReceipt] = useState<Receipt | null>(null);
  
  // Filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('Pending Manager Approval');
  
  // Review dialog state
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<any>(null);
  const [receipts, setReceipts] = useState<Receipt[]>([]);
  const [loadingReceipts, setLoadingReceipts] = useState(false);
  
  // Review form state
  const [decision, setDecision] = useState<'approve' | 'reject'>('approve');
  const [signature, setSignature] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isManager) {
      fetchAllSubmissions();
    }
  }, [isManager]);

  // ========================================
  // DATA FETCHING
  // ========================================

  const fetchAllSubmissions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await submissionsAPI.getAllForManager();
      
      setAllSubmissions(response);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load submissions');
      console.error('Error fetching submissions:', err);
    } finally {
      setLoading(false);
    }
  };

  // ========================================
  // FILTERING & SEARCH
  // ========================================

  const filteredSubmissions = useMemo(() => {
    let filtered = [...allSubmissions];

    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(s => s.status === statusFilter);
    }

    // Search by employee name or email
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(s => 
        s.submittedBy?.toLowerCase().includes(query) ||
        s.submittedByEmail?.toLowerCase().includes(query) ||
        s.userEmail?.toLowerCase().includes(query) ||
        s.displayName?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [allSubmissions, statusFilter, searchQuery]);

  // ========================================
  // HANDLERS
  // ========================================

  const handleOpenReview = async (submission: any) => {
    setSelectedSubmission(submission);
    setReviewDialogOpen(true);
    setDecision('approve');
    setSignature('');
    setNotes('');

    // Load receipts
    try {
      setLoadingReceipts(true);
      const receiptsData = await receiptsAPI.getReceipts(submission.submissionId);
      setReceipts(receiptsData || []);
    } catch (err) {
      console.error('Error loading receipts:', err);
      setReceipts([]);
    } finally {
      setLoadingReceipts(false);
    }
  };

  const handleViewReceipt = (receipt: Receipt) => {
    setSelectedReceipt(receipt);
    setViewReceiptDialogOpen(true);
  };

  const handleCloseReview = () => {
    if (!submitting) {
      setReviewDialogOpen(false);
      setSelectedSubmission(null);
      setReceipts([]);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedSubmission || !signature.trim()) {
      showError('Please provide your signature');
      return;
    }

    // Check if rejecting an archived program submission
    if (decision === 'reject' && selectedSubmission.programIsArchived) {
      const confirmed = window.confirm(
        '⚠️ WARNING: This program is archived.\n\n' +
        'If you reject this submission, the employee will NOT be able to see or resubmit it.\n\n' +
        'Consider approving with notes instead, or contact the employee directly.\n\n' +
        'Are you sure you want to reject this submission?'
      );
      
      if (!confirmed) {
        return;
      }
    }

    try {
      setSubmitting(true);

      await submissionsAPI.managerReview(
        selectedSubmission.submissionId,
        decision === 'approve',
        signature.trim(),
        notes.trim() || undefined
      );

      await fetchAllSubmissions();

      showSuccess(
        decision === 'approve' 
          ? 'Submission approved and sent to HR!' 
          : 'Submission rejected'
      );

      handleCloseReview();
    } catch (err: any) {
      showError(err.response?.data?.message || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  // ========================================
  // HELPER FUNCTIONS
  // ========================================

  const formatPeriod = (month?: number, year?: number): string => {
    if (!month || !year) return 'N/A';
    return dayjs().month(month - 1).year(year).format('MMMM YYYY');
  };

  const getEmployeeName = (submission: any): string => {
    return submission.submittedBy || submission.userEmail || 'Unknown User';
  };

  // ========================================
  // RENDER
  // ========================================

  if (!isManager) {
    return (
      <Alert severity="warning">
        You do not have permission to access this page. Manager role required.
      </Alert>
    );
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Manager Review
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review and approve expense submissions from your team
          </Typography>
        </Box>
      </Box>

      {/* Search and Filter Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search by employee name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                select
                fullWidth
                label="Status Filter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <FilterIcon />
                    </InputAdornment>
                  ),
                }}
              >
                <MenuItem value="Pending Manager Approval">Pending Manager Approval (Default)</MenuItem>
                <MenuItem value="All">All Submissions</MenuItem>
                <MenuItem value="Draft">Draft</MenuItem>
                <MenuItem value="Pending HR Approval">Pending HR Approval</MenuItem>
                <MenuItem value="Approved">Approved</MenuItem>
                <MenuItem value="Rejected">Rejected</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Typography variant="body2" color="text.secondary" align="right">
                Showing <strong>{filteredSubmissions.length}</strong> of <strong>{allSubmissions.length}</strong>
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Submissions Grid */}
      {filteredSubmissions.length === 0 ? (
        <Card>
          <CardContent>
            <Box display="flex" flexDirection="column" alignItems="center" py={4}>
              <ApproveIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {searchQuery || statusFilter !== 'Pending Manager Approval'
                  ? 'No submissions match your filters'
                  : 'All Caught Up!'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {searchQuery || statusFilter !== 'Pending Manager Approval'
                  ? 'Try adjusting your search or filter'
                  : 'No submissions pending your review at this time.'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={3}>
          {filteredSubmissions.map((submission) => (
            <Grid item xs={12} md={6} key={submission.submissionId}>
              <Card>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
                    <Box>
                      <Typography variant="h6" component="div">
                        {getEmployeeName(submission)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {submission.displayName}
                      </Typography>
                    </Box>
                    <Chip 
                      label={getStatusLabel(submission.status)} 
                      color={getStatusColor(submission.status)}
                      size="small" 
                    />
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Period
                      </Typography>
                      <Typography variant="body2">
                        {formatSubmissionPeriod(submission)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        ${submission.totalAmount.toFixed(2)}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Receipts
                      </Typography>
                      <Typography variant="body2">
                        {submission.receiptCount} item{submission.receiptCount !== 1 ? 's' : ''}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Submitted
                      </Typography>
                      <Typography variant="body2">
                        {submission.submittedDate 
                          ? dayjs(submission.submittedDate).format('MMM D, YYYY')
                          : 'N/A'}
                      </Typography>
                    </Grid>

                    {/* Allowance Information */}
                    {submission.allowanceInfo && submission.allowanceInfo.hasAllowance && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Allowance
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Typography variant="body2">
                            ${submission.allowanceInfo.usedAmount.toFixed(2)} / ${submission.allowanceInfo.programAllowance?.toFixed(2)}
                          </Typography>
                          <Chip 
                            label={`${submission.allowanceInfo.remainingAmount >= 0 ? 'Within' : 'Exceeds'} Budget`}
                            color={submission.allowanceInfo.remainingAmount >= 0 ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {submission.submissionNotes && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {submission.submissionNotes}
                      </Typography>
                    </Box>
                  )}

                  <Box display="flex" gap={1} mt={3}>
                    {submission.status === 'Pending Manager Approval' ? (
                      <Button
                        variant="contained"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleOpenReview(submission)}
                        fullWidth
                      >
                        Review
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        startIcon={<ViewIcon />}
                        onClick={() => navigate(`/submissions/${submission.submissionId}`)}
                        fullWidth
                      >
                        View Details
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Review Dialog */}
      <Dialog 
        open={reviewDialogOpen} 
        onClose={handleCloseReview}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Review Submission
          {selectedSubmission && (
            <>
              <Typography variant="body2" color="text.secondary">
                {getEmployeeName(selectedSubmission)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {selectedSubmission.displayName}
              </Typography>
            </>
          )}
        </DialogTitle>

        <DialogContent>
          {selectedSubmission && (
            <Box>
              {/* Submission Summary */}
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Period
                      </Typography>
                      <Typography variant="body1">
                        {formatPeriod(selectedSubmission.periodMonth, selectedSubmission.periodYear)}
                      </Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="text.secondary">
                        Total Amount
                      </Typography>
                      <Typography variant="h6" color="primary.main">
                        ${selectedSubmission.totalAmount.toFixed(2)}
                      </Typography>
                    </Grid>
                    
                    {/* Allowance in dialog */}
                    {selectedSubmission.allowanceInfo && selectedSubmission.allowanceInfo.hasAllowance && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary">
                          Allowance Status
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="body2">
                            ${selectedSubmission.allowanceInfo.usedAmount.toFixed(2)} / ${selectedSubmission.allowanceInfo.programAllowance?.toFixed(2)}
                          </Typography>
                          <Chip 
                            label={`${selectedSubmission.allowanceInfo.remainingAmount >= 0 ? 'Within Budget' : 'Over Budget'}`}
                            color={selectedSubmission.allowanceInfo.remainingAmount >= 0 ? 'success' : 'warning'}
                            size="small"
                          />
                        </Box>
                      </Grid>
                    )}
                  </Grid>

                  {selectedSubmission.submissionNotes && (
                    <Box mt={2}>
                      <Typography variant="caption" color="text.secondary">
                        Employee Notes
                      </Typography>
                      <Typography variant="body2">
                        {selectedSubmission.submissionNotes}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>

              {/* Receipts Table */}
              <Typography variant="subtitle2" gutterBottom>
                Receipts ({receipts.length})
              </Typography>

              {loadingReceipts ? (
                <Box display="flex" justifyContent="center" py={3}>
                  <CircularProgress size={24} />
                </Box>
              ) : receipts.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No receipts found
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Vendor</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Amount</TableCell>
                        <TableCell align="center">Receipt</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {receipts.map((receipt) => (
                        <TableRow key={receipt.receiptId}>
                          <TableCell>{receipt.vendor}</TableCell>
                          <TableCell>
                            {dayjs(receipt.receiptDate).format('MMM D, YYYY')}
                          </TableCell>
                          <TableCell>{receipt.expenseCategory}</TableCell>
                          <TableCell align="right">
                            ${receipt.amount.toFixed(2)}
                          </TableCell>
                          <TableCell align="center">
                            <Button
                              size="small"
                              startIcon={<ViewIcon />}
                              onClick={() => handleViewReceipt(receipt)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell colSpan={3} align="right">
                          <strong>Total:</strong>
                        </TableCell>
                        <TableCell align="right">
                          <strong>${selectedSubmission.totalAmount.toFixed(2)}</strong>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Decision */}
              <Typography variant="subtitle2" gutterBottom>
                Your Decision
              </Typography>
              <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value as 'approve' | 'reject')}>
                <FormControlLabel
                  value="approve"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <ApproveIcon color="success" />
                      <Typography>Approve and send to HR</Typography>
                    </Box>
                  }
                />
                <FormControlLabel
                  value="reject"
                  control={<Radio />}
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <RejectIcon color="error" />
                      <Typography>Reject and return to employee</Typography>
                    </Box>
                  }
                />
              </RadioGroup>

              {/* Notes */}
              <TextField
                fullWidth
                multiline
                rows={3}
                label={decision === 'reject' ? 'Rejection Reason (Required)' : 'Notes (Optional)'}
                placeholder={
                  decision === 'reject'
                    ? 'Explain why this submission is being rejected...'
                    : 'Add any comments or notes...'
                }
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                sx={{ mt: 2 }}
                required={decision === 'reject'}
              />

              {/* Signature */}
              <TextField
                fullWidth
                label="Digital Signature"
                placeholder="Type your full name"
                value={signature}
                onChange={(e) => setSignature(e.target.value)}
                sx={{ mt: 2 }}
                required
                helperText="By signing, you confirm your review decision"
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseReview} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={decision === 'approve' ? 'success' : 'error'}
            onClick={handleSubmitReview}
            disabled={submitting || !signature.trim() || (decision === 'reject' && !notes.trim())}
            startIcon={submitting ? <CircularProgress size={20} /> : null}
          >
            {submitting 
              ? 'Submitting...' 
              : decision === 'approve' 
                ? 'Approve Submission' 
                : 'Reject Submission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Receipt Dialog */}
      {selectedReceipt && (
        <ViewReceiptDialog
          open={viewReceiptDialogOpen}
          receipt={selectedReceipt}
          onClose={() => setViewReceiptDialogOpen(false)}
        />
      )}
    </Box>
  );
};

export default ManagerReview;